// src/Components/nav/index.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import HomeLogo from "../../assets/Logo.png"; // ✅ use your logo image
import { MdMenu, MdClose } from "react-icons/md";

const navLinks = ["Home", "About", "Experience", "Work", "Contact"];
const NAV_OFFSET = 90; // adjust if your navbar height changes

const Navbar = () => {
  const [active, setActive] = useState("Home"); // ✅ was "home"
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Prevent body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Active section tracking (respects offset)
  useEffect(() => {
    const onScroll = () => {
      navLinks.forEach((link) => {
        const section = document.getElementById(link);
        if (!section) return;

        const top = section.offsetTop - NAV_OFFSET - 60;
        const bottom = top + section.offsetHeight;

        if (window.scrollY >= top && window.scrollY < bottom) {
          setActive(link);
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // run once on load
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ✅ Scroll with offset so sections don't hide under navbar
  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
  }, []);

  const handleNavClick = (id) => {
    scrollToSection(id);
    setMenuOpen(false);
  };

  return (
    <header
      className="fixed top-0 left-0 w-full z-50"
      style={{ backgroundColor: "#030f12" }}
    >
      <div className="backdrop-blur-md Swim">
        <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
          {/* ✅ Logo */}
          <button
            onClick={() => handleNavClick("Home")}
            className="flex items-center"
            aria-label="Go to Home"
          >
            <img
              src={HomeLogo}
              alt="Sirisha Lokanath Raju"
              className="w-14 h-14 object-contain"
              style={{ objectFit: "contain" }}
            />
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link}
                onClick={() => handleNavClick(link)}
                className="relative text-sm font-medium transition-colors"
                style={{
                  color:
                    active === link
                      ? "rgb(45,212,191)"
                      : "rgba(226,232,240,0.85)",
                }}
              >
                {link}
                {active === link && (
                  <span className="absolute -bottom-2 left-0 w-full h-[2px] bg-teal-400 rounded-full" />
                )}
              </button>
            ))}
          </nav>

          {/* Desktop Resume */}
          <a
            href="/Resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex px-4 py-2 text-sm font-semibold text-teal-300 border border-teal-400/40 rounded-md hover:bg-teal-400/10 transition-all"
          >
            Resume
          </a>

          {/* ✅ Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((s) => !s)}
          >
            {menuOpen ? (
              <MdClose size={24} className="text-teal-300" />
            ) : (
              <MdMenu size={24} className="text-teal-300" />
            )}
          </button>
        </div>
      </div>

      {/* ✅ Mobile Drawer */}
      {menuOpen && (
        <>
          {/* Overlay */}
          <button
            aria-label="Close menu overlay"
            onClick={() => setMenuOpen(false)}
            className="fixed inset-0 bg-black/55 md:hidden"
            style={{ zIndex: 60 }}
          />

          {/* Panel */}
          <div
            className="fixed top-[72px] right-0 w-[86%] max-w-[360px] md:hidden border-l border-white/10 bg-[#030f12]/95 backdrop-blur-xl"
            style={{ zIndex: 70, height: "calc(100vh - 72px)" }}
          >
            <div className="p-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <button
                  key={link}
                  onClick={() => handleNavClick(link)}
                  className="text-left px-3 py-3 rounded-lg transition"
                  style={{
                    color:
                      active === link
                        ? "rgb(45,212,191)"
                        : "rgba(226,232,240,0.92)",
                    background:
                      active === link ? "rgba(45,212,191,0.08)" : "transparent",
                    border:
                      active === link
                        ? "1px solid rgba(45,212,191,0.22)"
                        : "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  {link}
                </button>
              ))}

              <a
                href="/Resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex justify-center px-4 py-3 text-sm font-semibold text-teal-300 border border-teal-400/40 rounded-md hover:bg-teal-400/10 transition-all"
                onClick={() => setMenuOpen(false)}
              >
                Resume
              </a>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Navbar;
